import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { logger } from '../lib/logger';
import { Search, Package, Users, Send, X } from 'lucide-react';

/**
 * Global search bar for admin. Debounced query → /api/admin/search.
 * On selecting a result, calls onNavigate(tab, itemId).
 */
export default function GlobalSearch({ onNavigate }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ products: [], buyers: [], quotes: [] });
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults({ products: [], buyers: [], quotes: [] }); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/search`, { params: { q } });
        setResults(data);
      } catch (err) {
        logger.warn('[GlobalSearch] Search failed:', err?.message);
      }
      finally { setLoading(false); }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const handler = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const totalResults = results.products.length + results.buyers.length + results.quotes.length;

  const handleSelect = (tab) => {
    setOpen(false);
    setQ('');
    onNavigate?.(tab);
  };

  return (
    <div ref={rootRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#3D2817]/40" />
        <input
          data-testid="global-search"
          type="text"
          placeholder="Search products, buyers, quotes…"
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="w-full pl-10 pr-9 py-2 bg-[#F2ECE0]/50 border border-transparent focus:border-[#C5A059] focus:bg-white outline-none text-sm rounded-sm transition-colors"
        />
        {q && (
          <button onClick={() => { setQ(''); setOpen(false); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3D2817]/40 hover:text-[#3D2817]">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && q.trim() && (
        <div data-testid="global-search-results" className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#E5D9C8] rounded-sm shadow-lg max-h-[70vh] overflow-y-auto z-50">
          {loading && <div className="p-4 text-sm text-[#3D2817]/50">Searching…</div>}
          {!loading && totalResults === 0 && (
            <div className="p-4 text-sm text-[#3D2817]/50">No results for "{q}".</div>
          )}
          {results.products.length > 0 && (
            <div>
              <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-[#C5A059] border-b border-[#E5D9C8] bg-[#F2ECE0]/50 flex items-center gap-2">
                <Package className="w-3 h-3" /> Products ({results.products.length})
              </div>
              {results.products.map(p => (
                <button key={p.id} data-testid={`search-product-${p.id}`}
                  onClick={() => handleSelect('products')}
                  className="w-full text-left px-4 py-2 hover:bg-[#F2ECE0] border-b border-[#E5D9C8]/50 last:border-0 transition-colors">
                  <div className="font-mono text-[10px] text-[#3D2817]/50">{p.sku}</div>
                  <div className="font-serif text-sm">{p.name}</div>
                </button>
              ))}
            </div>
          )}
          {results.buyers.length > 0 && (
            <div>
              <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-[#C5A059] border-b border-[#E5D9C8] bg-[#F2ECE0]/50 flex items-center gap-2">
                <Users className="w-3 h-3" /> Buyers ({results.buyers.length})
              </div>
              {results.buyers.map(b => (
                <button key={b.id} data-testid={`search-buyer-${b.id}`}
                  onClick={() => handleSelect('buyers')}
                  className="w-full text-left px-4 py-2 hover:bg-[#F2ECE0] border-b border-[#E5D9C8]/50 last:border-0 transition-colors">
                  <div className="font-serif text-sm">{b.company || b.name || b.email}</div>
                  <div className="text-xs text-[#3D2817]/50">{b.email} · {b.country}</div>
                </button>
              ))}
            </div>
          )}
          {results.quotes.length > 0 && (
            <div>
              <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-[#C5A059] border-b border-[#E5D9C8] bg-[#F2ECE0]/50 flex items-center gap-2">
                <Send className="w-3 h-3" /> Quotes ({results.quotes.length})
              </div>
              {results.quotes.map(q => (
                <button key={q.id} data-testid={`search-quote-${q.id}`}
                  onClick={() => handleSelect('quotes')}
                  className="w-full text-left px-4 py-2 hover:bg-[#F2ECE0] border-b border-[#E5D9C8]/50 last:border-0 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="font-serif text-sm">{q.company_name}</div>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-[#F2ECE0]">{q.status}</span>
                  </div>
                  <div className="text-xs text-[#3D2817]/50">{q.contact_name} · {q.country} · {new Date(q.created_at).toLocaleDateString()}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
